import { useState, useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { RefreshCw } from 'lucide-react'
import type { NotebookNote } from '../../../shared/types/notebook'
import RenderedMarkdown from './RenderedMarkdown'
import RichContentEditor from '../RichContentEditor'
import MeetingFollowupSuggestions from './MeetingFollowupSuggestions'

interface MeetingNoteViewProps {
  note: NotebookNote
  allNotes: NotebookNote[]
  onNavigate: (id: string) => void
  onUpdateNote: (noteId: string, updates: Partial<NotebookNote>) => void
  /** True when the user has edited the Notes tab since the AI summary was last (re)generated. */
  summaryStale?: boolean
  /** True while a regeneration is in flight; disables the chip to prevent re-triggering. */
  regenerating?: boolean
  /** Trigger a summary regeneration (kicked off by the chip on the Summary tab). */
  onRegenerate?: () => void
}

type Tab = 'summary' | 'notes' | 'transcript'

/** Parse meeting markdown into summary sections and transcript */
function parseMeetingContent(contentMd: string) {
  const sections = contentMd.split(/^## /m)
  const summaryParts: string[] = []
  let transcript = ''

  for (const section of sections) {
    if (section.startsWith('Transcript') || section.startsWith('Transkription')) {
      transcript = section.replace(/^(Transcript|Transkription)\n+/, '')
    } else if (section.trim()) {
      // Check if this is the separator (---) before transcript
      const trimmed = section.trim()
      if (trimmed === '---') continue
      summaryParts.push('## ' + section)
    }
  }

  // Clean up: remove trailing --- from the last summary section
  const summaryText = summaryParts.join('').replace(/\n---\s*$/, '')

  return { summary: summaryText, transcript }
}

export default function MeetingNoteView({
  note,
  allNotes,
  onNavigate,
  onUpdateNote,
  summaryStale,
  regenerating,
  onRegenerate,
}: MeetingNoteViewProps) {
  const { t } = useTranslation('recording')
  const [activeTab, setActiveTab] = useState<Tab>('summary')

  const { summary, transcript } = useMemo(
    () => parseMeetingContent(note.contentMd),
    [note.contentMd],
  )

  const [userNotes, setUserNotes] = useState(note.userNotesMd ?? '')

  const handleUserNotesChange = useCallback(
    (value: string) => {
      setUserNotes(value)
      onUpdateNote(note.id, { userNotesMd: value || null })
    },
    [note.id, onUpdateNote],
  )

  const handleToggleCheckbox = useCallback(
    (lineIndex: number) => {
      const lines = note.contentMd.split('\n')
      const line = lines[lineIndex]
      if (!line) return
      if (/- \[ \]/.test(line)) {
        lines[lineIndex] = line.replace('- [ ]', '- [x]')
      } else if (/- \[x\]/i.test(line)) {
        lines[lineIndex] = line.replace(/- \[x\]/i, '- [ ]')
      }
      onUpdateNote(note.id, { contentMd: lines.join('\n') })
    },
    [note.id, note.contentMd, onUpdateNote],
  )

  const tabs: { id: Tab; label: string }[] = [
    { id: 'summary', label: t('tabs.summary') },
    { id: 'notes', label: t('tabs.notes') },
    { id: 'transcript', label: t('tabs.transcript') },
  ]

  return (
    <div className="flex flex-col gap-4">
      {/* Tab bar */}
      <div className="flex border-b border-gold/10">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`px-4 py-2.5 text-sm font-medium transition-colors relative ${
              activeTab === tab.id
                ? 'text-gold'
                : 'text-pearl/60 hover:text-pearl/80'
            }`}
          >
            {tab.label}
            {activeTab === tab.id && (
              <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-gold rounded-full" />
            )}
          </button>
        ))}
      </div>

      {/* Tab content */}
      {activeTab === 'summary' && (
        <div className="space-y-4">
          {summaryStale && !regenerating && onRegenerate && (
            <button
              onClick={onRegenerate}
              className="flex items-center gap-2 w-full bg-gold/5 border border-gold/20 hover:bg-gold/10 hover:border-gold/30 rounded-xl px-4 py-3 text-left transition-colors"
            >
              <RefreshCw size={14} className="text-gold/80 shrink-0" />
              <span className="text-pearl/80 text-sm flex-1">
                {t('regenerate.notesStaleHint')}
              </span>
              <span className="text-gold text-sm font-medium">
                {t('regenerate.notesStaleAction')}
              </span>
            </button>
          )}
          <MeetingFollowupSuggestions entryId={note.id} refreshKey={note.updatedAt} />
          <RenderedMarkdown
            content={summary}
            allNotes={allNotes}
            onNavigate={onNavigate}
            onToggleCheckbox={handleToggleCheckbox}
          />
        </div>
      )}

      {activeTab === 'notes' && (
        <div className="min-h-[200px]">
          <RichContentEditor
            value={userNotes}
            onChange={handleUserNotesChange}
            allNotes={allNotes}
            onNavigate={onNavigate}
            autoFocus={false}
            className="text-pearl/80 text-sm leading-relaxed"
            minHeight="200px"
            placeholder={t('meetingNote.notesPlaceholder')}
          />
        </div>
      )}

      {activeTab === 'transcript' && (
        <div className="text-pearl/60 text-sm leading-relaxed whitespace-pre-wrap">
          {transcript || <span className="text-pearl/55 italic">{t('meetingNote.noTranscript')}</span>}
        </div>
      )}
    </div>
  )
}
